/* eslint-disable no-unsafe-optional-chaining */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { createSearchParams, Link, useSearchParams } from 'react-router-dom';
import { Trash, Users, ShieldCheck } from 'phosphor-react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { PageHeader } from '../../components/PageHeader';
import { IData, IPagination } from '../../types/shared';
import { DataTable } from '../../components/datatable';
import { TableBox } from '../../components/tableBox';
import { EmptyData } from '../../components/emptyData';
import { colors } from '../../theme/colors';
import { IGroup, useGroups } from '../../hooks/useGroups';
import { SearchModeBar, MODES_ID_NOME } from '../../components/SearchModeBar';
import { ActionsButton } from '../../components/buttons/ActionsButton';
import { useDialog } from '../../providers/dialog';
import { cleanFilters } from '../../utils/HandleFunctions';

export function GroupsPage() {
  const showDialog = useDialog();
  const { getGroups } = useGroups();
  const [searchMode, setSearchMode] = useState<'ID' | 'Nome'>('ID');
  const searchModeRef = useRef(searchMode);

  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);

  const [filters, setFilters] = useSearchParams(
    createSearchParams({
      page: '1',
      size: '10',
      search: '',
    }),
  );
  // const { mutateAsync } = useMutation({
  //   mutationFn: deleteGroup,
  // });

  const filtersFormMethods = useForm<{ search: string }>({
    mode: 'onChange',
    defaultValues: {
      search: filters.get('search') ?? '',
    },
  });

  const debounced = useDebounceCallback(values => {
    const searchValue = values.search?.trim() ?? '';
    const newFilters: Record<string, any> = {
      page: 1,
      size: 10,
    };

    if (searchValue) {
      const currentMode = searchModeRef.current;
      switch (currentMode) {
        case 'ID':
          newFilters.id = searchValue;
          break;
        case 'Nome':
          newFilters.nome = searchValue;
          break;
        default:
          break;
      }
    }

    setFilters(newFilters);
  }, 500);

  const queryKey = ['groups', cleanFilters(Object.fromEntries([...Array.from(filters)]))];
  const { data, isFetching } = useQuery<IData<IGroup[]>>({
    queryKey,
    queryFn: getGroups,
  });

  const { items: groups, ...pagination } = data?.data ?? { items: [], ...({} as IPagination) };

  const columns = useMemo<ColumnDef<IGroup>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
      },
      {
        accessorKey: 'nome',
        header: 'Nome',
      },
      {
        id: 'actions',
        cell: ({ row }) => {
          const group = row.original;

          return (
            <ActionsButton aria-label="actions">
              <Link to={`/configuracoes/permissoes?grupo=${group.id}`}>
                <Button variant="unstyled" p="6px" display="flex" alignItems="center" gap={2}>
                  <ShieldCheck size={20} weight="bold" />
                  <Text>Permissões</Text>
                </Button>
              </Link>

              <Button
                variant="unstyled"
                p="6px"
                display="flex"
                alignItems="center"
                gap={2}
                onClick={() =>
                  showDialog({
                    status: 'warning',
                    title: `Tem certeza que deseja remover o grupo ${group.nome}?`,
                    description: 'Essa ação não pode ser desfeita.',
                    cancelLabel: 'Cancelar',
                    // async onConfirm() {
                    //   await mutateAsync(String(group?.id));
                    //   setFilters(oldState => ({ ...oldState, page: 1 }));
                    // },
                  })
                }
              >
                <Trash size={20} weight="bold" />
                <Text>Remover</Text>
              </Button>
            </ActionsButton>
          );
        },
      },
    ],
    [],
  );

  useEffect(() => {
    const subscription = filtersFormMethods.watch(debounced);
    return () => subscription.unsubscribe();
  }, [filtersFormMethods.watch]);

  useEffect(() => {
    debounced(filtersFormMethods.getValues());
  }, [searchMode]);

  return (
    <Box>
      <PageHeader
        title="Grupos"
        subtitle="Crie grupos de trabalho e gerencie as permissões de acesso de cada grupo."
      />

      {!filters.get('search') &&
      !filters.get('id') &&
      !filters.get('nome') &&
      groups.length === 0 &&
      !isFetching ? (
        <EmptyData
          title="Não existem Grupos"
          subtitle="Cadastre os grupos"
          buttonLabel="Adicionar novo grupo"
          icon={<Users size={90} color={colors.primary} />}
        />
      ) : (
        <TableBox>
          <div className="flex justify-between gap-2">
            <FormProvider {...filtersFormMethods}>
              <div className="flex w-full flex-col gap-3 sm:w-fit sm:flex-row sm:items-center sm:gap-2">
                <SearchModeBar
                  modes={MODES_ID_NOME}
                  mode={searchMode}
                  onModeChange={m => setSearchMode(m as typeof searchMode)}
                  value={filtersFormMethods.watch('search') ?? ''}
                  onValueChange={v =>
                    filtersFormMethods.setValue('search', v, { shouldDirty: true })
                  }
                />
              </div>
            </FormProvider>
          </div>

          <div className="border border-b-divider" />

          <DataTable
            columns={columns}
            data={groups}
            pagination={pagination}
            emptyMsg="Nenhum grupo encontrado."
            loadingMsg="Carregando grupos..."
            isFetching={isFetching}
          />
        </TableBox>
      )}
    </Box>
  );
}
